import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { reports, type TrialBalanceRow } from '@/api/documents';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useGridColumnWidths } from '@/hooks/useGridColumnWidths';
import DatePopover from '@/components/DatePopover';

function startOfMonth(): string {
  const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0);
  return toLocalIso(d);
}
function startOfNextMonth(): string {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + 1); d.setHours(0, 0, 0, 0);
  return toLocalIso(d);
}
function toLocalIso(d: Date): string {
  const tzOffset = -d.getTimezoneOffset();
  const sign = tzOffset >= 0 ? '+' : '-';
  const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${sign}${pad(tzOffset / 60)}:${pad(tzOffset % 60)}`;
}

const fmt = (v: string) => Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function TrialBalancePage() {
  const { t } = useTranslation();
  // UX γ Y-deep P2 (2026-05-22): регламентний звіт — single-org dropdown
  // з фільтром по reports.regulated.view. Семантика: trial balance
  // подається у держоргани per-org окремо, агрегація методологічно
  // сумнівна (на відміну від управлінського P&L який aggregate).
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('reports.regulated.view');
  const [orgId, setOrgId] = useState('');
  const [from, setFrom] = useState<string>(() => '2026-02-01T00:00:00+02:00');
  const [to, setTo] = useState<string>(() => '2026-03-01T00:00:00+02:00');
  // Персист ширин колонок ag-Grid (дзеркало DataTable-персисту).
  const colW = useGridColumnWidths('trial-balance');

  // Default org — перша з availableOrgs (де є reports.regulated.view).
  // Це усуває auto-fetch на random org з 53 і відповідне 403-toast.
  if (!orgId && availableOrgs[0]) setOrgId(availableOrgs[0].id);

  const tb = useQuery({
    queryKey: ['trial-balance', orgId, from, to],
    queryFn: () => reports.trialBalance({ organization_id: orgId, date_from: from, date_to: to }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColDef<TrialBalanceRow>[]>(() => [
    { field: 'account_code', headerName: t('tb.code'), width: 90, pinned: 'left' },
    { field: 'account_name', headerName: t('tb.account'), flex: 1, minWidth: 280, pinned: 'left' },
    {
      headerName: t('tb.opening'),
      children: [
        { field: 'opening_balance_dt', headerName: t('tb.dt'), width: 130, valueFormatter: (p: { value: unknown }) => fmt(p.value as string), cellStyle: { textAlign: 'right' } },
        { field: 'opening_balance_cr', headerName: t('tb.cr'), width: 130, valueFormatter: (p: { value: unknown }) => fmt(p.value as string), cellStyle: { textAlign: 'right' } },
      ],
    },
    {
      headerName: t('tb.turnover'),
      children: [
        { field: 'turnover_dt', headerName: t('tb.dt'), width: 130, valueFormatter: (p: { value: unknown }) => fmt(p.value as string), cellStyle: { textAlign: 'right' } },
        { field: 'turnover_cr', headerName: t('tb.cr'), width: 130, valueFormatter: (p: { value: unknown }) => fmt(p.value as string), cellStyle: { textAlign: 'right' } },
      ],
    },
    {
      headerName: t('tb.closing'),
      children: [
        { field: 'closing_balance_dt', headerName: t('tb.dt'), width: 130, valueFormatter: (p: { value: unknown }) => fmt(p.value as string), cellStyle: { textAlign: 'right' } },
        { field: 'closing_balance_cr', headerName: t('tb.cr'), width: 130, valueFormatter: (p: { value: unknown }) => fmt(p.value as string), cellStyle: { textAlign: 'right' } },
      ],
    },
  ], [t]);

  const isBalanced = tb.data && tb.data.totals.turnover_dt === tb.data.totals.turnover_cr;

  // UX γ Y-deep P2: warning page якщо user не має reports.regulated.view
  // у жодній org (інакше — порожній dropdown і dead state).
  if (!permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>{t('tb.title')}</h2>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав переглядати регламентний звіт у жодній з
            ваших організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{t('tb.title')}</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => { setFrom(startOfMonth()); setTo(startOfNextMonth()); }}>
            {t('tb.this_month')}
          </button>
          <button onClick={() => tb.refetch()}>{t('tb.refresh')}</button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>{t('tb.organization')}</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {availableOrgs.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють регламентні звіти)
            </small>
          )}
        </div>
        <div className="field">
          <label>{t('tb.from')}</label>
          <DatePopover withTime value={from.slice(0, 16)} onChange={(v) => setFrom(v)} />
        </div>
        <div className="field">
          <label>{t('tb.to')}</label>
          <DatePopover withTime value={to.slice(0, 16)} onChange={(v) => setTo(v)} />
        </div>
      </div>

      <div className="ag-theme-quartz" style={{ height: 'calc(100vh - 380px)', minHeight: 400, marginTop: '1rem' }}>
        <AgGridReact<TrialBalanceRow>
          rowData={tb.data?.rows ?? []}
          columnDefs={cols}
          loading={tb.isLoading}
          onColumnResized={colW.onColumnResized}
          onGridReady={(e) => { colW.applyTo(e.api); }}
        />
      </div>

      {tb.data && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.75rem', fontWeight: 600 }}>
          <span>
            {t('tb.totals')}: Дт {fmt(tb.data.totals.turnover_dt)} · Кт {fmt(tb.data.totals.turnover_cr)}
          </span>
          <span style={{ color: isBalanced ? '#15803d' : '#b91c1c' }}>
            {isBalanced ? '✓ ' + t('tb.balanced') : '✗ ' + t('tb.not_balanced')}
          </span>
        </div>
      )}
    </div>
  );
}
